import { useState } from "react";
import { DatabaseBackup, Download, CloudUpload, ShieldCheck, MapPin, FileClock, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, download } from "../lib/api";
import { useGet } from "../lib/hooks";
import { PageHeader, Stat } from "../components/Common";
import { RecordDialog } from "../components/RecordDialog";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../lib/auth";
import { DatabaseMove } from "../components/DatabaseMove";

const SETTINGS_FIELDS = [
  { key: "privacy_officer", label: "Privacy officer (APP 1)" }, { key: "breach_contact", label: "NDB breach response contact" },
  { key: "retention_years", label: "Record retention (years)", type: "number" }, { key: "daily_backup", label: "Daily backup", type: "checkbox" },
  { key: "sharepoint_auto_push", label: "Auto-push backups to SharePoint", type: "checkbox" },
];

const CHECKLIST = [
  "Documents and images stored in Azure Australia East (Sydney)",
  "Daily encrypted backups stored in Azure Australia Southeast (Melbourne), 7-year retention",
  "Live database on MongoDB Atlas, Australia East (Sydney)",
  "Australian Privacy Principles (Privacy Act 1988) — consent captured on forms",
  "Spam Act 2003 — unsubscribe/STOP handling blocks further messages",
  "Notifiable Data Breaches scheme — breach contact & incident category",
  "NDIS Practice Standards — incident register with reportable flag",
  "Immutable audit trail of all create / update / delete / export actions",
  "AUD pricing with 10% GST itemised tax invoices (ABN)",
];

export default function Compliance() {
  const { user } = useAuth();
  const [sum, reloadSum] = useGet("/compliance/summary");
  const [backups, reloadB] = useGet("/compliance/backups");
  const [audit] = useGet("/compliance/audit");
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!sum) return null;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/compliance/backup"); toast.success(`Backup created · ${data.records} records`); reloadB(); reloadSum(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const verify = async (id) => {
    try { const { data } = await api.post(`/compliance/backups/${id}/verify`); toast[data.result === "passed" ? "success" : "error"](`Restore check ${data.result}: ${data.detail}`); reloadB(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sp = async (id) => { try { const { data } = await api.post(`/compliance/backups/${id}/sharepoint`); toast.success(`Pushed to ${data.path} (MOCK)`); reloadB(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader overline="Sovereignty" title="Australian Compliance Centre" desc="Data residency, backups, SharePoint archive, audit trail and privacy controls.">
        <Button variant="secondary" onClick={() => setEdit(true)} data-testid="compliance-settings-btn">Policy settings</Button>
        <Button onClick={run} disabled={busy} data-testid="run-backup-btn"><DatabaseBackup className="h-4 w-4 mr-1" />{busy ? "Backing up…" : "Backup now"}</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Where data lives" value={<span className="text-sm leading-snug block">Files: {sum.documents_region}<br />Backups: {sum.backups_region}</span>} sub={`Database: ${sum.database_region}`} icon={MapPin} testid="stat-data-region" />
        <Stat label="Backups" value={sum.backups} sub={sum.last_backup ? `Last ${fmtDate(sum.last_backup.created_at)}` : "None yet"} icon={DatabaseBackup} testid="stat-backups" />
        <Stat label="Audit events" value={sum.audit_events} icon={FileClock} testid="stat-audit-events" />
        <Stat label="Consented contacts" value={sum.consented_contacts} sub={`${sum.opted_out} opted out`} icon={UserCheck} accent testid="stat-consented" />
      </div>
      <Tabs defaultValue="backups">
        <TabsList className="mb-6">
          <TabsTrigger value="backups" data-testid="tab-backups">Backups</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit trail</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist">Compliance checklist</TabsTrigger>
          {user.role === "agency_owner" && <TabsTrigger value="dbmove" data-testid="tab-db-move">Database move</TabsTrigger>}
        </TabsList>
        {user.role === "agency_owner" && <TabsContent value="dbmove"><DatabaseMove /></TabsContent>}
        <TabsContent value="backups" className="space-y-2">
          {!backups?.length && <div className="rounded-md border p-8 text-center text-muted-foreground">No backups yet. Click “Backup now”.</div>}
          {backups?.map((b) => (
            <div key={b.id} className="rounded-md border bg-card p-4 flex flex-wrap items-center gap-4" data-testid={`backup-${b.id}`}>
              <DatabaseBackup className="h-5 w-5 text-primary" />
              <div className="flex-1 min-w-[200px]"><div className="text-sm">{fmtDate(b.created_at)} · {b.records} records · {b.size_kb} KB</div>
                <div className="text-[11px] font-mono text-muted-foreground break-all">sha256 {b.sha256}</div>
                <div className="text-[11px] text-muted-foreground" data-testid={`backup-location-${b.id}`}>{b.blob_name ? `Stored in ${b.region}` : "Older backup, stored in the app database"}</div>
                {b.last_check && <div className={`text-[11px] ${b.last_check.result === "passed" ? "text-emerald-400" : "text-red-400"}`} data-testid={`backup-check-${b.id}`}>Restore check {b.last_check.result} · {fmtDate(b.last_check.at)} · {b.last_check.detail}</div>}
                {b.sharepoint_path && <div className="text-[11px] text-primary">{b.sharepoint_path}</div>}</div>
              <Button size="sm" variant="outline" onClick={() => verify(b.id)} data-testid={`verify-backup-${b.id}`}><ShieldCheck className="h-3.5 w-3.5 mr-1" />Restore check</Button>
              <Button size="sm" variant="secondary" onClick={() => download(`/compliance/backups/${b.id}/download`, `backup-${b.id.slice(0, 8)}.json`)} data-testid={`download-backup-${b.id}`}><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
              <Button size="sm" variant="outline" onClick={() => sp(b.id)} data-testid={`backup-sharepoint-${b.id}`}><CloudUpload className="h-3.5 w-3.5 mr-1" />SharePoint</Button>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="audit">
          <div className="rounded-md border bg-card/60 max-h-[60vh] overflow-y-auto" data-testid="audit-trail">
            {(audit || []).map((a) => (
              <div key={a.id} className="flex flex-wrap justify-between gap-2 border-b px-4 py-2 text-sm">
                <span><span className="font-mono text-primary">{a.action}</span> {a.entity} <span className="text-muted-foreground">{a.detail}</span></span>
                <span className="text-xs text-muted-foreground">{a.user_email} · {fmtDate(a.created_at)}</span>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="checklist">
          <div className="rounded-md border bg-card p-5 space-y-3">
            {CHECKLIST.map((c) => <div key={c} className="flex items-center gap-3 text-sm"><ShieldCheck className="h-4 w-4 text-primary shrink-0" />{c}</div>)}
            <div className="text-xs text-muted-foreground pt-3 border-t">Privacy officer: {sum.settings.privacy_officer || "[not set]"} · Breach contact: {sum.settings.breach_contact || "[not set]"} · Retention: {sum.settings.retention_years} years</div>
          </div>
        </TabsContent>
      </Tabs>
      {edit && <RecordDialog open onOpenChange={setEdit} title="Compliance policy settings" fields={SETTINGS_FIELDS} initial={sum.settings}
        onSave={async (f) => { try { await api.put("/compliance/settings", f); toast.success("Saved"); reloadSum(); } catch (e) { toast.error(errMsg(e)); throw e; } }} />}
    </div>
  );
}
